<template>
  <b-card-group
    id="subscription-upgrade-card"
    deck
    class="mx-lg-2"
    v-if="apparatusesToAdd && apparatusesToAdd.length && subscriptionProduct"
  >
    <b-card
      :class="`paid-subs-info-card mb-0 sub-card mx-1 ${isHighlighted ? 'new' : 'current'}`"
      body-class="subs-info-card-body"
      data-test-hook="upgrade-subscription-card"
    >
      <template #header>
        <div data-test-hook="upgrade-plan-header" class="plan-header">
          {{ subscriptionProduct.name }}
        </div>
      </template>
      <b-row v-if="annualPaidProduct && monthlyPaidProduct">
        <b-col>
          <CadenceSelectionTabs
            :annual-paid-product="annualPaidProduct"
            :monthly-paid-product="monthlyPaidProduct"
            :selected-product="subscriptionProduct"
            @setSelectedProduct="setSelectedProduct"
          />
        </b-col>
      </b-row>
      <b-row class="align-items-center mb-2 mx-0">
        <b-col v-if="!hideBadge" class="px-0">
          <b-badge
            v-if="isNewPlan"
            variant="primary"
            class="plan-badge"
            data-test-hook="upgrade-subscription-card-new-plan-badge"
            >{{ $t('xNewPlan') }}</b-badge
          >
          <b-badge
            v-else
            variant="light"
            class="border-primary text-primary plan-badge"
            data-test-hook="upgrade-subscription-card-current-plan-badge"
            >{{ $t('xCurrentPlan') }}</b-badge
          >
        </b-col>
        <b-col class="px-0 price">
          <h1 class="mb-0 pb-0" data-test-hook="upgrade-subscription-card-cost" :class="hideBadge ? 'solo-price' : ''">
            {{ subscriptionProduct.cost | formatCurrency }}<span class="duration-label"> /{{ durationLabel }}</span>
            <b-badge
              v-if="hideBadge && annualPaidProduct && subscriptionProduct.productId === annualPaidProduct.productId"
              variant="primary"
              class="plan-badge ml-2 align-middle"
              data-test-hook="subscription-savings-badge"
            >
              {{ $t('xSavePercent') }}
            </b-badge>
          </h1>
        </b-col>
      </b-row>
      <div class="subscriptionCardText">
        <b-row>
          <b-col>
            <strong
              v-if="subscriptionProduct && subscriptionProduct.description"
              data-test-hook="existing-subscription-modal-premium-monitoring-max-products-text"
            >
              {{ subscriptionProduct.description }}
            </strong>
          </b-col>
        </b-row>
        <b-row v-for="(apparatus, index) in existingApparatuses" :key="`current-${index}`">
          <b-col class="mt-2">
            <div class="d-flex">
              <i class="fas fa-fw fa-check-circle check-icon mr-2" />
              <p>{{ apparatus.name }}</p>
            </div>
          </b-col>
        </b-row>
        <b-row v-for="(apparatus, index) in apparatusesToAdd" :key="`new-${index}`">
          <ExpandableHsbSubscriptionFeaturesList
            v-if="apparatus.type === 0 || apparatus.apparatusType === 0"
            :id="`${apparatus.apparatusId}-${isNewPlan ? 'current' : 'new'}`"
            :generator-name="apparatus.name"
            :is-covered="isNewPlan || areAllCovered"
          />
          <ExpandableTankSubscriptionFeaturesList
            v-else-if="apparatus.type === 2 || apparatus.apparatusType === 2"
            :id="`${apparatus.apparatusId}-${isNewPlan ? 'current' : 'new'}`"
            :tank-name="apparatus.name"
            :is-covered="isNewPlan || areAllCovered"
          />
        </b-row>
      </div>
    </b-card>
  </b-card-group>
</template>

<script>
import CadenceSelectionTabs from './CadenceSelectionTabs.vue'
import ExpandableHsbSubscriptionFeaturesList from './ExpandableHsbSubscriptionFeaturesList.vue'
import ExpandableTankSubscriptionFeaturesList from './ExpandableTankSubscriptionFeaturesList.vue'

export default {
  /**
   * subscriptionProduct: the PRODUCT itself, with name, cost, id etc
   * existingApparatuses: apparatuses that are already enrolled or exist on an active subscription
   * apparatusesToAdd: new apparatuses that are being enrolled or added to a subscription
   * isNewPlan: determines what the pill says, as well as whether apparatuses are marked as supported or not (for upgrade views)
   * isHightlighted: for determining outline color - single cards will always be grey (not highlighted), but when more than 1 is shown, the "upgraded" sub is orange
   * areAllCovered: overrides isNewPlan for purposes of marking apparatuses as included in a plan (for existing plan view)
   * annualPaidProduct: annual product to pass to cadence tabs
   * monthlyPaidProduct: monthly product to pass to cadence tabs
   * hideBadge: hides the 'new/current plan' badge and just shows the price
   */
  props: [
    'subscriptionProduct',
    'existingApparatuses',
    'apparatusesToAdd',
    'isNewPlan',
    'isHighlighted',
    'areAllCovered',
    'monthlyPaidProduct',
    'annualPaidProduct',
    'hideBadge'
  ],
  emits: ['setSelectedProduct'],
  components: {
    ExpandableHsbSubscriptionFeaturesList,
    ExpandableTankSubscriptionFeaturesList,
    CadenceSelectionTabs
  },
  methods: {
    setSelectedProduct(product) {
      // only for use IF there is a monthly and an annual product
      this.$emit('setSelectedProduct', product)
    }
  },
  computed: {
    durationLabel() {
      if (this.subscriptionProduct?.durationInMonths === 1) {
        return this.$t('xMonth').toLowerCase()
      }

      return this.$t('xYear').toLowerCase()
    }
  }
}
</script>
<style lang="scss">
#subscription-upgrade-card {
  margin: 0 !important;
  height: 100%;

  .paid-subs-info-card {
    &.current {
      border: 2px solid var(--carbon-300) !important;

      .plan-header {
        background-color: var(--carbon-300);
      }
    }

    &.new {
      border: 2px solid var(--brand-400) !important;

      .plan-header {
        background-color: var(--brand-400) !important;
      }
    }
  }

  .price {
    text-align: center;

    & > h1 {
      font-size: 30px;
    }
  }

  .plan-badge {
    font-size: 14px;

    &.border-primary {
      border: 2px solid;
    }

    &:not(.border-primary) {
      padding: 5px;
    }
  }

  .solo-price {
    font-weight: bold;
  }

  .subs-info-card-body {
    padding: 0px 25px 25px !important;
  }

  .card-header {
    align-items: stretch;
    padding: 0;
    border-bottom: 0;
    height: 40px;
    padding: 0px 0px 0px 0px !important;
  }

  .card-title {
    margin-bottom: 0;
  }

  .duration-label {
    font-weight: normal;
    font-size: 16px;
  }

  .plan-header {
    text-align: center;
    padding: 5px 0px;
    font-size: 24px;
    line-height: 1.35;
    font-weight: 500;
  }

  .sub-radio-dot {
    font-size: 0.875rem !important;
  }

  .sub-premium-radio-duration {
    font-size: 16px !important;
    text-transform: lowercase !important;
  }

  .subscriptionCardText {
    font-size: 16px !important;
  }

  i {
    font-size: 1.125em;
    color: var(--carbon-600);
  }

  .check-icon {
    color: var(--blue-700);
  }
}
</style>
